import React, { useEffect, useState } from "react";
import "./AddCalendar.css";
import { Flex, SafeArea } from "../../components/layout";
import { Button, FileInput, Form, Input, Title } from "../../components/ui";
import { db, storage } from "../../components/firebase/firebase";
import { setDoc, doc, getDoc, deleteDoc } from "firebase/firestore";
import { slugify } from "../../components/function";
import Notification from "../../components/ui/alert/Notification";
import { useNavigate, useParams } from "react-router-dom";
import { optimizeImage } from "../../functions/image";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import SimpleEditor from "../../components/common/editor/SimpleEditor";
import Editor from "../../components/common/editor/Editor";
import { logEvent } from "../../utils/logger";

function AddCalendar() {
  const [title, setTitle] = useState("");
  const [dateStart, setDateStart] = useState("");
  const [dateEnd, setDateEnd] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]); // Stocke les images pour l'upload
  const [imageFiles, setImageFiles] = useState([]); // Pour l'éditeur riche
  const [notification, setNotification] = useState(null);

  const navigate = useNavigate();

  const { id } = useParams();

  // Fonction pour extraire le texte brut du HTML
  const extractTextFromHTML = (html) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  useEffect(() => {
    const fetchCalendar = async () => {
      const docRef = doc(db, "calendar", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setTitle(data.title);
        setDateStart(data.dateStart);
        setDateEnd(data.dateEnd);
        setDescription(data.description || "");
        setImages(
          data?.img
            ? data.img.map((img) => ({ preview: img, file: null }))
            : [],
        );
      } else {
        console.log("No such document!");
      }
    };
    if (id) {
      fetchCalendar();
    }
  }, [id]);

  const uploadImageToStorage = async (img) => {
    const optimizedImage = await optimizeImage(img.file);
    const storageRef = ref(
      storage,
      `calendar/flyers/${Date.now()}_${img.file.name}`,
    );
    await uploadBytes(storageRef, optimizedImage);
    const url = await getDownloadURL(storageRef);
    return url;
  };

  const handleChange = (e) => {
    const files = e.target.files;
    const newImages = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const preview = URL.createObjectURL(file);
      newImages.push({ preview, file });
    }

    setImages(newImages);
  };

  // Sauvegarde de l'article dans Firestore avec images finales
  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    const titleText = extractTextFromHTML(title);
    if (!titleText.trim()) {
      setLoading(false);
      alert("Le titre est obligatoire.");
      return;
    }

    const tempLinksImages = [];

    for (const img of images) {
      try {
        if (img.file) {
          const url = await uploadImageToStorage(img);
          tempLinksImages.push(url);
        } else {
          tempLinksImages.push(img.preview); // Si c'est déjà une URL Firebase, on la garde
        }
      } catch (error) {
        logEvent("ERROR", "DB", "Calendar image upload failed", {
          error: error.message,
          stack: error.stack,
          fileName: img.file?.name,
        });
        console.error("Erreur lors de l'upload :", error);
      }
    }

    try {
      const newSlug = slugify(titleText);
      if (id && id !== newSlug) {
        await deleteDoc(doc(db, "calendar", id));
      }
      await setDoc(doc(db, "calendar", newSlug), {
        title,
        dateStart,
        dateEnd,
        description,
        img: tempLinksImages,
        createdAt: Date.now(),
      });
      setTitle("");
      setDateStart("");
      setDateEnd("");
      setDescription("");
      setImages([]);
      setNotification({
        type: "success",
        message: "Action réalisée avec succès.",
      });
      logEvent(
        "INFO",
        "DB",
        id ? "Calendar event updated" : "Calendar event created",
        {
          slug: newSlug,
          title: titleText,
          dateStart,
          dateEnd,
          imagesCount: tempLinksImages.length,
          editedFrom: id || null,
        },
      );
      if (id) {
        navigate("/calendar/manage");
      }
    } catch (error) {
      logEvent("ERROR", "DB", "Calendar event save failed", {
        error: error.message,
        stack: error.stack,
        title: extractTextFromHTML(title),
        isEdit: !!id,
      });
      console.error("Erreur lors de la sauvegarde :", error);
      setNotification({ type: "error", message: "Échec de la sauvegarde." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeArea className="AddCalendar">
      <Notification
        state={notification}
        message={notification?.message || ""}
        type={notification?.type || "success"}
        onClose={() => setNotification(null)}
      />
      <Flex type="col" gap={50}>
        <Title level="h3">{id ? "Modifier" : "Ajouter"} un événement</Title>
        <Form>
          <Flex type="col" gap={20}>
            <SimpleEditor label="Titre" value={title} onChange={setTitle} />
            <Flex gap={20}>
              <Input
                type="date"
                label="Date de début"
                value={dateStart}
                onChange={(e) => setDateStart(e.target.value)}
              />
              <Input
                type="date"
                label="Date de fin"
                value={dateEnd}
                onChange={(e) => setDateEnd(e.target.value)}
              />
            </Flex>
            <Editor
              label="Description (optionnelle)"
              value={description}
              onChange={setDescription}
              setImageFiles={setImageFiles}
            />
            <FileInput
              label="Photos (optionnelles)"
              multiple
              onChange={handleChange}
              value={images}
              accept=".jpg,.png,.webp"
            />
            <Flex type="end-center" className="ctn-btn" gap={15}>
              <Button skin="nobg" to={id ? "/calendar/manage" : "/"}>
                Annuler
              </Button>
              <Button onClick={handleSave} loading={loading}>
                Publier
              </Button>
            </Flex>
          </Flex>
        </Form>
      </Flex>
    </SafeArea>
  );
}

export default AddCalendar;
